import { useState } from "react";
import { Check, Pencil, Trash2, Ban, Loader2, X, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../../lib/api";
import { EMAIL_STATUS_STYLES, fmtDT, inputCls, BlockedLabel } from "../../lib/outreach";

export const StatusPill = ({ status, testid }) => (
  <span data-testid={testid} className={`text-[11px] px-2.5 py-0.5 rounded-full border ${EMAIL_STATUS_STYLES[status] || ""}`}>
    {status}
  </span>
);

const Meta = ({ label, value }) => (
  <div><span className="text-slate-500">{label}: </span><span className="text-slate-300">{value}</span></div>
);

export default function EmailCard({ email, isAdmin, onChange }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ to: email.to, subject: email.subject, body: email.body });
  const [busy, setBusy] = useState(false);
  const id = email.id;

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); setEditing(false); onChange(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const approve = () => act(() => api.post(`/outreach/emails/${id}/approve`), "Approved — added to the send queue");
  const recheck = () => act(() => api.post(`/outreach/emails/${id}/recheck`), "Checks re-run");
  const save = () => act(() => api.put(`/outreach/emails/${id}`, form), "Draft updated");
  const remove = () => window.confirm("Reject and delete this draft?") && act(() => api.delete(`/outreach/emails/${id}`), "Draft deleted");
  const cancel = () => window.confirm("Cancel this scheduled email?") && act(() => api.post(`/outreach/emails/${id}/cancel`), "Email cancelled");

  return (
    <div data-testid={`outreach-email-${email.step}`} className="rounded-xl border border-slate-800 bg-[#0B1120]/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="font-display font-bold text-amber-400">Email {email.step}</span>
          <StatusPill status={email.status} testid={`outreach-email-${email.step}-status`} />
          {email.status === "Draft" && <BlockedLabel reason={email.blocked_reason} testid={`outreach-email-${email.step}-blocked`} />}
        </div>
        {isAdmin && (
          <div className="flex gap-1.5">
            {email.status === "Draft" && !editing && (
              <>
                <button data-testid={`approve-email-${email.step}`} disabled={busy} onClick={approve} className="btn-primary rounded-lg px-3 py-1.5 text-xs inline-flex items-center gap-1">
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Approve
                </button>
                <button data-testid={`edit-email-${email.step}`} onClick={() => setEditing(true)} className="rounded-lg px-3 py-1.5 text-xs border border-slate-700 text-slate-300 hover:border-amber-500/50 inline-flex items-center gap-1 transition-colors">
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                {email.blocked_reason && (
                  <button data-testid={`recheck-email-${email.step}`} disabled={busy} onClick={recheck} className="rounded-lg px-3 py-1.5 text-xs border border-slate-700 text-slate-300 hover:border-amber-500/50 inline-flex items-center gap-1 transition-colors">
                    <RefreshCw className="w-3.5 h-3.5" /> Recheck
                  </button>
                )}
                <button data-testid={`delete-email-${email.step}`} onClick={remove} className="rounded-lg px-3 py-1.5 text-xs border border-slate-700 text-red-300 hover:bg-red-500/10 inline-flex items-center gap-1 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> Reject
                </button>
              </>
            )}
            {["Scheduled", "Failed"].includes(email.status) && (
              <button data-testid={`cancel-email-${email.step}`} onClick={cancel} className="rounded-lg px-3 py-1.5 text-xs border border-slate-700 text-slate-300 hover:text-red-300 inline-flex items-center gap-1 transition-colors">
                <Ban className="w-3.5 h-3.5" /> Cancel
              </button>
            )}
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <input data-testid={`edit-email-${email.step}-to`} className={inputCls} value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} />
          <input data-testid={`edit-email-${email.step}-subject`} className={inputCls} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <textarea data-testid={`edit-email-${email.step}-body`} rows={8} className={inputCls} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          <div className="flex gap-2">
            <button data-testid={`save-email-${email.step}`} disabled={busy} onClick={save} className="btn-primary rounded-lg px-3 py-1.5 text-xs inline-flex items-center gap-1">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
            </button>
            <button onClick={() => setEditing(false)} className="rounded-lg px-3 py-1.5 text-xs border border-slate-700 text-slate-300 inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> Cancel</button>
          </div>
        </div>
      ) : (
        <>
          <p className="text-xs text-slate-400 mb-1">To: <span className="text-slate-200">{email.to}</span></p>
          <p data-testid={`outreach-email-${email.step}-subject`} className="text-sm font-semibold text-white mb-2">{email.subject}</p>
          <pre className="whitespace-pre-wrap font-sans text-sm text-slate-300 bg-[#131C31] border border-slate-800 rounded-lg p-3 max-h-64 overflow-auto">{email.body}</pre>
        </>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 mt-3 text-[11px]">
        <Meta label="Created" value={fmtDT(email.created_at)} />
        <Meta label="Approved" value={fmtDT(email.approved_at)} />
        <Meta label="Scheduled" value={fmtDT(email.scheduled_at)} />
        <Meta label="Sent" value={fmtDT(email.sent_at)} />
      </div>
      {email.sent_subject && email.sent_subject !== email.subject && (
        <p data-testid={`outreach-email-${email.step}-sent-subject`} className="text-xs text-slate-400 mt-2">
          Sent as reply in thread: <span className="text-slate-200">{email.sent_subject}</span>
        </p>
      )}
      {email.error && <p className="text-xs text-red-300 mt-2">Error: {email.error}</p>}
      {email.stopped_reason && <p className="text-xs text-slate-500 mt-2">Stopped: {email.stopped_reason}</p>}
    </div>
  );
}
